import * as React from 'react';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {getCheckColor} from '../../../constants';

const styles = {
    qualityLogs: {
        margin: '12px 16px 8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fafafa'
    },
    qualityLogHeader: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap'
    },
    qualityLogCardWrap: {
        flex: '1 1 320px',
        minWidth: '280px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    qualityLogCard: {
        width: '100%',
        minHeight: '150px',
        boxSizing: 'border-box',
        padding: '14px 16px',
        border: '1px solid #dedede',
        borderTop: '3px solid #607d8b',
        borderRadius: '4px',
        backgroundColor: '#fff',
        color: '#333',
        fontSize: '14px',
        lineHeight: 1.45
    },
    qualityLogTitle: {
        color: '#263238',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.3,
        marginBottom: '8px'
    },
    qualityLogDate: {
        display: 'inline-block',
        marginBottom: '12px',
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: '#eef3f5',
        color: '#455a64',
        fontSize: '12px'
    },
    qualityLogRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    qualityLogRow: {
        display: 'grid',
        gridTemplateColumns: '110px minmax(0, 1fr)',
        gap: '12px',
        borderTop: '1px solid #f0f0f0',
        paddingTop: '6px'
    },
    qualityLogLabel: {
        color: '#777',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase'
    },
    qualityLogValue: {
        minWidth: 0,
        overflowWrap: 'break-word'
    },
    qualityLogButton: {
        alignSelf: 'flex-start',
        minWidth: '180px'
    },
    qualityLogActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap'
    },
    qualityLogButtonNops: {
        borderColor: '#c56f6f',
        color: '#8d3d3d'
    },
    qualityLogButtonRq: {
        borderColor: '#b99a35',
        color: '#80651a'
    }
};

const tryParseJson = value => {
    if (typeof value !== 'string') {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
};

const formatDate = value => {
    if (!value) {
        return 'Not available';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

const prettifyKey = key => key
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, character => character.toUpperCase());

const formatValue = value => {
    const parsed = tryParseJson(value);

    if (parsed === null || parsed === undefined || parsed === '') {
        return 'None';
    }

    if (Array.isArray(parsed)) {
        return parsed.length ? parsed.map(formatValue).join(', ') : 'None';
    }

    if (typeof parsed === 'object') {
        return Object.keys(parsed).length
            ? Object.entries(parsed).map(([key, entryValue]) => `${prettifyKey(key)}: ${formatValue(entryValue)}`).join('; ')
            : 'None';
    }

    if (typeof parsed === 'boolean') {
        return parsed ? 'Yes' : 'No';
    }

    return String(parsed);
};

const renderDataLines = data => {
    const parsed = tryParseJson(data);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return (
            <div style={styles.qualityLogRow}>
                <span style={styles.qualityLogLabel}>Data</span>
                <span style={styles.qualityLogValue}>{formatValue(parsed)}</span>
            </div>
        );
    }

    return Object.entries(parsed).map(([key, value]) => (
        <div key={key} style={styles.qualityLogRow}>
            <span style={styles.qualityLogLabel}>{prettifyKey(key)}</span>
            <span style={styles.qualityLogValue}>{formatValue(value)}</span>
        </div>
    ));
};

const BASE = 'https://hetzner.pandonia-global-network.org';

const backendUrl = targetPath => `${BASE}${targetPath}`;

export const NopsCheckDetail = ({data}) => {
    const {panId, currentNopsCheck} = data;
    const checkDate = currentNopsCheck && currentNopsCheck.checkDate;
    const nopsUrl = backendUrl(`/nops/${panId}/new/`);
    const nopsHistoryUrl = backendUrl(`/nops/${panId}/history/`);
    const nopsColor = getCheckColor(checkDate, 'nopsCheck');

    return (
        <div style={styles.qualityLogs}>
            <div style={styles.qualityLogHeader}>
                <Typography variant="h6">NOps weekly check</Typography>
            </div>
            <div style={styles.qualityLogCardWrap}>
                <div style={{...styles.qualityLogCard, borderTopColor: nopsColor}}>
                    <span style={styles.qualityLogDate}>{formatDate(checkDate)}</span>
                    <div style={styles.qualityLogRows}>
                        <div style={styles.qualityLogRow}>
                            <span style={styles.qualityLogLabel}>Summary</span>
                            <span style={styles.qualityLogValue}>{formatValue(currentNopsCheck && currentNopsCheck.checkSummary)}</span>
                        </div>
                        {renderDataLines(currentNopsCheck && currentNopsCheck.checkData)}
                    </div>
                </div>
                <div style={styles.qualityLogActions}>
                    <Button
                        variant="outlined"
                        href={nopsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{...styles.qualityLogButton, ...styles.qualityLogButtonNops}}
                        endIcon={<OpenInNewIcon />}
                    >
                        Open NOps check
                    </Button>
                    <Button
                        variant="outlined"
                        href={nopsHistoryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{...styles.qualityLogButton, ...styles.qualityLogButtonNops}}
                        endIcon={<OpenInNewIcon />}
                    >
                        History
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const RoutineQcCheckDetail = ({data}) => {
    const {currentRqCheck} = data;
    const routineQcId = currentRqCheck && currentRqCheck.routineQcId;
    const lastChecked = currentRqCheck && currentRqCheck.lastChecked;
    const rqUrl = routineQcId ? backendUrl(`/rq_checks/${routineQcId}/`) : undefined;
    const rqColor = getCheckColor(lastChecked, 'rqCheck');

    return (
        <div style={styles.qualityLogs}>
            <div style={styles.qualityLogHeader}>
                <Typography variant="h6">Routine QC check</Typography>
            </div>
            <div style={styles.qualityLogCardWrap}>
                <div style={{...styles.qualityLogCard, borderTopColor: rqColor}}>
                    <span style={styles.qualityLogDate}>{formatDate(lastChecked)}</span>
                    <div style={styles.qualityLogRows}>
                        {renderDataLines(currentRqCheck && currentRqCheck.data)}
                    </div>
                </div>
                <Button
                    variant="outlined"
                    href={rqUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!routineQcId}
                    style={{...styles.qualityLogButton, ...styles.qualityLogButtonRq}}
                    endIcon={<OpenInNewIcon />}
                >
                    Open Routine QC
                </Button>
            </div>
        </div>
    );
};
